// Each room's light stays in its own room (Grey, 2026-11-03: "Light from quarters are leaking into corridors").
// The ship has one sky light and one "sun" for every room. A room's own lighting (its warmth or colour from the F menu, and a printed light
// switch turned down) used to be laid on those two lights for whoever stood in the room, so everything they could see took it: the
// corridor through the door, the next room, the white fixtures all over the ship; and a camera behind you saw the corridor turn the
// room's colour through the wall. Now every surface takes the light of the compartment it faces, wherever you stand:
//  - a map of the compartments (shared/ship.ts compartmentOf, the same rooms the server and the manifest use) is drawn into a texture,
//    an 8 cm square a texel on each of four levels: the lower hangar, Deck 1, Deck 2, the upper hangar
//  - a row of each room's light: its colour against the ship's (r, g, b) and its switch (a: 1 up, down to 0)
//  - every lit material in the game's scene looks itself up there: the point a few centimetres off the surface along its normal, so a
//    wall's corridor face takes the corridor's light and its room face the room's. The direct light (sky and sun) takes the colour and the
//    switch, the light probe's reflections the switch only (as before), and the white fixtures' glow the colour
// Cost: two texel reads a pixel on lit materials, nothing on the CPU while nothing changes. The map is only drawn the first time a room
// has a light of its own (a few hundred milliseconds of work, spread over frames), a few kB uploaded while a room's light fades.
import { Constants, MaterialPluginBase, PBRMaterial, RawTexture, RegisterMaterialPlugin, StandardMaterial, type Material, type Scene, type UniformBuffer } from "@babylonjs/core";
import { ShaderLanguage } from "@babylonjs/core/Materials/shaderLanguage.js";
import { compartmentOf, blenderToScene } from "../shared/ship.js";

export type RGBK = [number, number, number, number];
// the map: Blender x from X0, Blender y from Y0, RES metres a texel, W x H a level; the levels by Blender z as compartmentOf bands them
const X0 = -112, Y0 = -189.6, RES = 0.125, W = 1024, H = 768, LEVELS = 4, N = 256;
const LEVEL_Z = [-3, 3.4, 8.5, 14];            // where each level is drawn: below -1 (the lower hangar), Deck 1, Deck 2 (above 5.5), above 12.5 (the upper hangar)
const OFF = 0.12;                               // metres off the surface along its normal: which side of a wall a face is on
const SLICE_MS = 4;                             // the map's drawing, a few milliseconds a frame

const GLSL_DEFS = `
#ifdef ROOMLIT
uniform sampler2D roomMap;
uniform sampler2D roomRatios;
vec4 roomLitAt(vec3 p) {
  float lay = p.y < -1.0 ? 0.0 : (p.y > 12.5 ? 3.0 : (p.y > 5.5 ? 2.0 : 1.0));
  vec2 g = vec2((-p.x - (${X0.toFixed(3)})) * ${(1 / RES).toFixed(3)}, (-p.z - (${Y0.toFixed(3)})) * ${(1 / RES).toFixed(3)});
  if (g.x < 0.0 || g.y < 0.0 || g.x >= ${W.toFixed(1)} || g.y >= ${H.toFixed(1)}) { return vec4(1.0); }
  float r = texelFetch(roomMap, ivec2(int(g.x), int(g.y) + int(lay) * ${H}), 0).r;
  return texelFetch(roomRatios, ivec2(int(r * 255.0 + 0.5), 0), 0);
}
#endif`;
const WGSL_DEFS = `
#ifdef ROOMLIT
var roomMapSampler: sampler;
var roomMap: texture_2d<f32>;
var roomRatiosSampler: sampler;
var roomRatios: texture_2d<f32>;
fn roomLitAt(p: vec3f) -> vec4f {
  var lay: i32 = 1;
  if (p.y < -1.0) { lay = 0; } else if (p.y > 12.5) { lay = 3; } else if (p.y > 5.5) { lay = 2; }
  let g = vec2f((-p.x - (${X0.toFixed(3)})) * ${(1 / RES).toFixed(3)}, (-p.z - (${Y0.toFixed(3)})) * ${(1 / RES).toFixed(3)});
  if (g.x < 0.0 || g.y < 0.0 || g.x >= ${W.toFixed(1)} || g.y >= ${H.toFixed(1)}) { return vec4f(1.0); }
  let r = textureLoad(roomMap, vec2i(i32(g.x), i32(g.y) + lay * ${H}), 0).r;
  return textureLoad(roomRatios, vec2i(i32(r * 255.0 + 0.5), 0), 0);
}
#endif`;
// PBR: the direct light (diffuse, specular, sheen, clear coat) takes the room's colour and switch, the probe's light the switch, a fixture's glow the colour
const PBR_GLSL = `
#ifdef ROOMLIT
{ vec4 roomL = roomLitAt(vPositionW + normalW * ${OFF.toFixed(3)});
#ifndef UNLIT
  vec3 roomK = roomL.rgb * roomL.a;
  finalDiffuse *= roomK;
#ifdef SPECULARTERM
  finalSpecularScaled *= roomK;
#endif
#ifdef SHEEN
  finalSheenScaled *= roomK;
#endif
#ifdef CLEARCOAT
  finalClearCoatScaled *= roomK;
#endif
#ifdef REFLECTION
  finalIrradiance *= roomL.a;
  finalRadianceScaled *= roomL.a;
#ifdef CLEARCOAT
  clearcoatOut.finalClearCoatRadianceScaled *= roomL.a;
#endif
#if defined(SHEEN) && defined(ENVIRONMENTBRDF)
  sheenOut.finalSheenRadianceScaled *= roomL.a;
#endif
#endif
#endif
#ifdef ROOMLIT_FIXTURE
  finalEmissive *= roomL.rgb;
#endif
}
#endif`;
const PBR_WGSL = `
#ifdef ROOMLIT
{ let roomL = roomLitAt(fragmentInputs.vPositionW + normalW * ${OFF.toFixed(3)});
#ifndef UNLIT
  let roomK = roomL.rgb * roomL.a;
  finalDiffuse *= roomK;
#ifdef SPECULARTERM
  finalSpecularScaled *= roomK;
#endif
#ifdef SHEEN
  finalSheenScaled *= roomK;
#endif
#ifdef CLEARCOAT
  finalClearCoatScaled *= roomK;
#endif
#ifdef REFLECTION
  finalIrradiance *= roomL.a;
  finalRadianceScaled *= roomL.a;
#ifdef CLEARCOAT
  clearcoatOut.finalClearCoatRadianceScaled *= roomL.a;
#endif
#if defined(SHEEN) && defined(ENVIRONMENTBRDF)
  sheenOut.finalSheenRadianceScaled *= roomL.a;
#endif
#endif
#endif
#ifdef ROOMLIT_FIXTURE
  finalEmissive *= roomL.rgb;
#endif
}
#endif`;
// StandardMaterial (the game's own lit props): the whole lit colour
const STD_GLSL = `
#ifdef ROOMLIT
{ vec4 roomL = roomLitAt(vPositionW + normalW * ${OFF.toFixed(3)}); color.rgb *= roomL.rgb * roomL.a; }
#endif`;
const STD_WGSL = `
#ifdef ROOMLIT
{ let roomL = roomLitAt(fragmentInputs.vPositionW + normalW * ${OFF.toFixed(3)}); color = vec4f(color.rgb * roomL.rgb * roomL.a, color.a); }
#endif`;

class RoomLitPlugin extends MaterialPluginBase {
  constructor(material: Material, private field: RoomField, private pbr: boolean) {
    super(material, "RoomLit", 190, { ROOMLIT: false, ROOMLIT_FIXTURE: false }); this.registerForExtraEvents = true; this._enable(true);
  }
  override getClassName() { return "RoomLitPlugin"; }
  override isCompatible(lang: ShaderLanguage) { return lang === ShaderLanguage.GLSL || lang === ShaderLanguage.WGSL; }
  override prepareDefines(defines: Record<string, unknown>) {
    const m = this._material as unknown as { unlit?: boolean; disableLighting?: boolean };
    const fixture = this.field.isFixture(this._material);
    defines.ROOMLIT = fixture || !(m.unlit || m.disableLighting); defines.ROOMLIT_FIXTURE = this.pbr && fixture;
  }
  override getSamplers(samplers: string[]) { samplers.push("roomMap", "roomRatios"); }
  override bindForSubMesh(ubo: UniformBuffer) { this.hardBindForSubMesh(ubo); }
  override hardBindForSubMesh(ubo: UniformBuffer) { ubo.setTexture("roomMap", this.field.map); ubo.setTexture("roomRatios", this.field.ratios); }
  override getCustomCode(shaderType: string, lang?: ShaderLanguage): Record<string, string> | null {
    if (shaderType !== "fragment") return null;
    const wgsl = lang === ShaderLanguage.WGSL;
    return { CUSTOM_FRAGMENT_DEFINITIONS: wgsl ? WGSL_DEFS : GLSL_DEFS,
      [this.pbr ? "CUSTOM_FRAGMENT_BEFORE_FINALCOLORCOMPOSITION" : "CUSTOM_FRAGMENT_BEFORE_FOG"]: this.pbr ? (wgsl ? PBR_WGSL : PBR_GLSL) : (wgsl ? STD_WGSL : STD_GLSL) };
  }
}

export class RoomField {
  readonly map: RawTexture; readonly ratios: RawTexture;
  private grid = new Uint8Array(W * H * LEVELS);
  private ratioData = new Float32Array(N * 4).fill(1);
  private index = new Map<string, number>([["Outside", 0]]);   // (0: no room's light, always 1)
  private building: Generator<void, void, void> | null = null; private built = false;
  private want: Map<string, RGBK> | null = null; private dirty = false;
  private fixtures = new WeakSet<Material>();
  constructor(private scene: Scene) {
    this.map = new RawTexture(this.grid, W, H * LEVELS, Constants.TEXTUREFORMAT_R, scene, false, false, Constants.TEXTURE_NEAREST_SAMPLINGMODE, Constants.TEXTURETYPE_UNSIGNED_BYTE);
    this.ratios = new RawTexture(this.ratioData, N, 1, Constants.TEXTUREFORMAT_RGBA, scene, false, false, Constants.TEXTURE_NEAREST_SAMPLINGMODE, Constants.TEXTURETYPE_FLOAT);
    for (const t of [this.map, this.ratios]) { t.wrapU = Constants.TEXTURE_CLAMP_ADDRESSMODE; t.wrapV = Constants.TEXTURE_CLAMP_ADDRESSMODE; t.name = "room_light_" + (t === this.map ? "map" : "ratios"); }
    const engine = scene.getEngine(), ok = engine.isWebGPU || ((engine as unknown as { webGLVersion?: number }).webGLVersion ?? 2) >= 2;   // (texel reads: WebGL 2 or WebGPU)
    if (ok) RegisterMaterialPlugin("RoomLit", (m) => {
      if (m.getScene() !== scene) return null;   // (the game's scene only: the labs, the bench and the previews keep their own lights)
      const k = m.getClassName(); return k === "PBRMaterial" ? new RoomLitPlugin(m, this, true) : k === "StandardMaterial" ? new RoomLitPlugin(m, this, false) : null;
    });
  }
  // a white light fixture (client/roomlight.ts finds them): its glow takes its room's colour
  markFixture(m: Material) { if (this.fixtures.has(m)) return; this.fixtures.add(m); if (m instanceof PBRMaterial || m instanceof StandardMaterial) m.markAsDirty(Constants.MATERIAL_MiscDirtyFlag); }
  isFixture(m: Material) { return this.fixtures.has(m); }
  // each room's light now, by compartment name; a room left out is the ship's (1, 1, 1, 1)
  set(rooms: Map<string, RGBK>) {
    this.want = rooms; this.dirty = true;
    if (!this.built && !this.building && [...rooms.values()].some((v) => v.some((x) => Math.abs(x - 1) > 1e-4))) this.building = this.draw();
  }
  get ready() { return this.built; }
  get names() { return [...this.index.keys()]; }
  // the room's light at a scene point, as a surface there would take it (tests)
  at(x: number, y: number, z: number): { room: string; light: RGBK } | null {
    if (!this.built) return null;
    const lay = y < -1 ? 0 : y > 12.5 ? 3 : y > 5.5 ? 2 : 1, gx = Math.floor((-x - X0) / RES), gy = Math.floor((-z - Y0) / RES);
    if (gx < 0 || gy < 0 || gx >= W || gy >= H) return { room: "", light: [1, 1, 1, 1] };
    const i = this.grid[(gy + lay * H) * W + gx], room = [...this.index].find(([, v]) => v === i)?.[0] ?? "";
    return { room, light: [this.ratioData[i * 4], this.ratioData[i * 4 + 1], this.ratioData[i * 4 + 2], this.ratioData[i * 4 + 3]] };
  }
  tick() {
    if (this.building) { const t0 = performance.now(); while (performance.now() - t0 < SLICE_MS) { if (this.building.next().done) { this.building = null; this.built = true; this.map.update(this.grid); this.dirty = true; break; } } }
    if (!this.dirty || !this.built || !this.want) return;
    this.dirty = false; this.ratioData.fill(1);
    for (const [name, v] of this.want) { const i = this.index.get(name); if (i === undefined || i === 0) continue; this.ratioData.set(v, i * 4); }
    this.ratios.update(this.ratioData);
  }
  // the compartments drawn into the map: every metre first, then each metre square whose corners disagree texel by texel (no room is
  // narrower than a metre and a half, so a square whose corners agree is that room all through)
  private *draw(): Generator<void, void, void> {
    const S = Math.round(1 / RES), CW = W / S, CH = H / S;
    const idOf = (name: string) => { let i = this.index.get(name); if (i === undefined) { i = this.index.size < N ? this.index.size : 0; this.index.set(name, i); } return i; };
    const at = (bx: number, by: number, bz: number) => idOf(compartmentOf(blenderToScene({ x: bx, y: by, z: bz })));
    for (let L = 0; L < LEVELS; L++) {
      const z = LEVEL_Z[L], corner = new Uint8Array((CW + 1) * (CH + 1));
      for (let j = 0; j <= CH; j++) { for (let i = 0; i <= CW; i++) corner[j * (CW + 1) + i] = at(X0 + i, Y0 + j, z); if (j % 8 === 0) yield; }
      for (let j = 0; j < CH; j++) {
        for (let i = 0; i < CW; i++) {
          const a = corner[j * (CW + 1) + i], b = corner[j * (CW + 1) + i + 1], c = corner[(j + 1) * (CW + 1) + i], d = corner[(j + 1) * (CW + 1) + i + 1];
          for (let v = 0; v < S; v++) {
            const row = (L * H + j * S + v) * W + i * S;
            if (a === b && a === c && a === d) { this.grid.fill(a, row, row + S); continue; }
            for (let u = 0; u < S; u++) this.grid[row + u] = at(X0 + (i * S + u + 0.5) * RES, Y0 + (j * S + v + 0.5) * RES, z);
          }
        }
        yield;
      }
    }
  }
}
